import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle, CheckCircle2, ArrowLeft, CalendarDays,
  ShieldCheck, Clock, Users, CreditCard, Lock,
  Plus, Percent, Minus, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CALENDAR_MODIFIERS_CN = { range: 'bg-[#e2e8f0]/20 text-white rounded-md' };
const CALENDAR_CLASS_NAMES = {
  months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
  month: 'space-y-4',
  caption: 'flex justify-center pt-1 relative items-center',
  caption_label: 'text-sm font-medium text-white',
  nav: 'space-x-1 flex items-center',
  nav_button: 'h-7 w-7 bg-transparent p-0 text-gray-400 hover:text-white border border-slate-600 rounded-md inline-flex items-center justify-center',
  nav_button_previous: 'absolute left-1',
  nav_button_next: 'absolute right-1',
  table: 'w-full border-collapse space-y-1',
  head_row: 'flex',
  head_cell: 'text-gray-500 rounded-md w-9 font-normal text-[0.8rem]',
  row: 'flex w-full mt-2',
  cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-transparent',
  day: 'h-9 w-9 p-0 font-normal text-gray-300 hover:bg-slate-700 rounded-md inline-flex items-center justify-center',
  day_selected: 'bg-[#e2e8f0] text-slate-900 hover:bg-[#cbd5e1] font-semibold',
  day_today: 'ring-1 ring-[#e2e8f0] text-white',
  day_outside: 'text-slate-600',
  day_disabled: 'text-slate-700 opacity-40 cursor-not-allowed hover:bg-transparent',
  day_hidden: 'invisible',
};

export default function BookPackage() {
  const { packageId } = useParams();
  const [pkg, setPkg] = useState(null);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [numDays, setNumDays] = useState(1);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [addonUnavailable, setAddonUnavailable] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', venueAccess: '', additionalInfo: '', loadInTime: '', loadOutTime: '', loadInAccess: '' });

  // Fetch package, availability, and addons
  useEffect(() => {
    const load = async () => {
      try {
        const [pkgRes, availRes, addonsRes, cfgRes] = await Promise.all([
          fetch(`${API_URL}/api/packages/${packageId}`),
          fetch(`${API_URL}/api/availability/${packageId}`),
          fetch(`${API_URL}/api/packages/${packageId}/addons`),
          fetch(`${API_URL}/api/config/booking`),
        ]);
        if (!pkgRes.ok) throw new Error('Package not found');
        setPkg(await pkgRes.json());

        const availData = await availRes.json();
        const blocked = [
          ...availData.booked_dates,
          ...availData.blackouts.map((b) => b.event_date),
        ];
        setUnavailableDates(blocked);

        if (addonsRes.ok) setAddons(await addonsRes.json());
        if (cfgRes.ok) {
          const cfg = await cfgRes.json();
          setPaymentsEnabled(cfg.payments_enabled);
        }
      } catch {
        toast.error('Failed to load package details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [packageId]);

  // Fetch addon availability when addon changes
  useEffect(() => {
    if (!selectedAddon) {
      setAddonUnavailable([]);
      return;
    }
    const fetchAddonAvail = async () => {
      try {
        const res = await fetch(`${API_URL}/api/availability/${selectedAddon.id}`);
        if (!res.ok) return;
        const data = await res.json();
        setAddonUnavailable([
          ...data.booked_dates,
          ...data.blackouts.map((b) => b.event_date),
        ]);
      } catch (err) { console.error('Failed to fetch addon availability:', err); }
    };
    fetchAddonAvail();
  }, [selectedAddon]);

  // Merged unavailable dates (main + addon)
  const allUnavailable = useMemo(() => {
    const set = new Set([...unavailableDates, ...addonUnavailable]);
    return [...set];
  }, [unavailableDates, addonUnavailable]);

  // Charge calculation — mirrors backend get_charge_pence logic
  const getCharge = (p) => {
    if (!p) return 0;
    if (p.quote_only || p.deposit_hold) return p.deposit_pence || 0;
    return p.price_from_pence || p.deposit_pence || 0;
  };

  const pricing = useMemo(() => {
    if (!pkg) return { mainPerDay: 0, addonPerDay: 0, dailyTotal: 0, subtotal: 0, discountPct: 0, discountAmount: 0, exVat: 0, vat: 0, total: 0, isDeposit: false, hasQuoteOnly: false, hasDepositHold: false, balanceDuePence: 0 };
    const mainPerDay = getCharge(pkg);
    const addonPerDay = getCharge(selectedAddon);
    const dailyTotal = mainPerDay + addonPerDay;
    const subtotal = dailyTotal * numDays;
    const discountPct = numDays >= 7 ? 20 : numDays >= 3 ? 10 : 0;
    const discountAmount = Math.round(subtotal * discountPct / 100);
    const exVat = subtotal - discountAmount;
    const vat = Math.round(exVat * 0.2);
    const total = exVat + vat;
    const hasQuoteOnly = !!(pkg.quote_only || selectedAddon?.quote_only);
    const hasDepositHold = !!(pkg.deposit_hold || selectedAddon?.deposit_hold);
    const isDeposit = hasQuoteOnly || hasDepositHold;
    // For deposit_hold, compute the known balance (ex-VAT)
    let balanceDuePence = 0;
    if (hasDepositHold) {
      const mainFull = pkg.price_from_pence || mainPerDay;
      const addonFull = selectedAddon ? (selectedAddon.price_from_pence || addonPerDay) : 0;
      const fullSubtotal = (mainFull + addonFull) * numDays;
      const fullAfterDiscount = Math.round(fullSubtotal * (100 - discountPct) / 100);
      balanceDuePence = fullAfterDiscount - exVat;
    }
    return { mainPerDay, addonPerDay, dailyTotal, subtotal, discountPct, discountAmount, exVat, vat, total, isDeposit, hasQuoteOnly, hasDepositHold, balanceDuePence };
  }, [pkg, selectedAddon, numDays]);

  const pence = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v / 100);

  // Range highlighting for calendar
  const rangeHighlight = useMemo(() => {
    if (!selectedDate || numDays <= 1) return [];
    const dates = [];
    for (let i = 1; i < numDays; i++) {
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  }, [selectedDate, numDays]);

  // Check if any date in the range is unavailable
  const rangeConflict = useMemo(() => {
    if (!selectedDate) return false;
    for (let i = 0; i < numDays; i++) {
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (allUnavailable.includes(iso)) return true;
    }
    return false;
  }, [selectedDate, numDays, allUnavailable]);

  // Disabled day matchers for calendar
  const disabledDays = useMemo(() => {
    const dates = allUnavailable.map((d) => {
      const [y, m, day] = d.split('-').map(Number);
      return new Date(y, m - 1, day);
    });
    return [{ before: new Date() }, ...dates];
  }, [allUnavailable]);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const isPequod = pkg && [1, 3, 5].includes(pkg.id);

  const pequodValid = !isPequod || (
    form.loadInTime.trim() && form.loadOutTime.trim() && form.loadInAccess && form.venueAccess.trim()
  );

  const canSubmit = selectedDate && !rangeConflict && form.name.trim() && form.email.trim() && form.phone.trim() && pequodValid && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);

    const eventDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

    try {
      if (paymentsEnabled) {
        // Stripe checkout path
        const body = {
          package_id: Number(packageId),
          event_date: eventDate,
          num_days: numDays,
          customer_name: form.name,
          customer_email: form.email,
          customer_phone: form.phone,
          origin_url: window.location.origin,
        };
        if (selectedAddon) body.addon_package_id = selectedAddon.id;
        if (form.venueAccess.trim()) body.venue_access_notes = form.venueAccess;
        if (form.additionalInfo.trim()) body.additional_info = form.additionalInfo;
        if (form.loadInTime.trim()) body.load_in_time = form.loadInTime;
        if (form.loadOutTime.trim()) body.load_out_time = form.loadOutTime;
        if (form.loadInAccess) body.load_in_access = form.loadInAccess;

        const res = await fetch(`${API_URL}/api/bookings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Booking failed');
        }

        const data = await res.json();
        window.location.href = data.checkout_url;
      } else {
        // Enquiry path (payments disabled)
        const body = {
          package_id: Number(packageId),
          package_name: pkg.name,
          event_date: eventDate,
          num_days: numDays,
          customer_name: form.name,
          customer_email: form.email,
          customer_phone: form.phone,
          charge_ex_vat_pence: pricing.exVat,
          vat_pence: pricing.vat,
          charge_inc_vat_pence: pricing.total,
          discount_percent: pricing.discountPct,
        };
        if (selectedAddon) {
          body.addon_package_id = selectedAddon.id;
          body.addon_package_name = selectedAddon.name;
        }
        if (form.venueAccess.trim()) body.venue_access_notes = form.venueAccess;
        if (form.additionalInfo.trim()) body.additional_info = form.additionalInfo;
        if (form.loadInTime.trim()) body.load_in_time = form.loadInTime;
        if (form.loadOutTime.trim()) body.load_out_time = form.loadOutTime;
        if (form.loadInAccess) body.load_in_access = form.loadInAccess;

        const res = await fetch(`${API_URL}/api/booking-enquiry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to submit request');
        }

        setEnquirySubmitted(true);
      }
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#e2e8f0] animate-spin" />
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4 heading-font">Package Not Found</h2>
          <Link to="/packages">
            <Button className="bg-[#e2e8f0] text-slate-900">Back to Packages</Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatRange = () => {
    if (!selectedDate) return null;
    const start = selectedDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
    if (numDays === 1) return start;
    const end = new Date(selectedDate);
    end.setDate(end.getDate() + numDays - 1);
    return `${start} — ${end.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' })}`;
  };

  return (
    <div className="min-h-screen bg-slate-900 pt-28 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/packages" className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-white transition mb-8 text-sm" data-testid="back-to-packages">
          <ArrowLeft className="w-4 h-4" /> Back to Packages
        </Link>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* LEFT: Package summary */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-[#1e293b] border-slate-700 overflow-hidden" data-testid="package-summary-card">
              {pkg.mainImage && (
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={pkg.mainImage} alt={pkg.name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-5 space-y-4">
                <div>
                  <Badge className="bg-[#e2e8f0] text-slate-800 mb-2 text-xs">{pkg.tier}</Badge>
                  <h1 className="text-2xl font-bold text-white heading-font">{pkg.name}</h1>
                  <p className="text-sm text-gray-400 mt-1">{pkg.description}</p>
                </div>
                <div className="flex items-center gap-2 text-gray-300 text-sm">
                  <Users className="w-4 h-4 text-[#94a3b8]" /> {pkg.capacity}
                </div>
                <div className="border-t border-slate-600 pt-4 space-y-1.5">
                  <h3 className="text-sm font-semibold text-[#cbd5e1] mb-2">What&apos;s Included</h3>
                  {pkg.included.map((item, i) => (
                    <div key={`incl-${item}`} className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" /> {item}
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-600 pt-4">
                  <p className="text-xs text-[#94a3b8] mb-1">Full package price</p>
                  <p className="text-xl font-bold text-white">{pkg.priceRange}</p>
                  <p className="text-xs text-gray-500">excl. VAT &middot; per day</p>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 bg-[#1e293b] rounded-lg px-3 py-2.5 border border-slate-700">
                <ShieldCheck className="w-4 h-4 text-green-400" />
                <span className="text-xs text-gray-300">Secure payment</span>
              </div>
              <div className="flex items-center gap-2 bg-[#1e293b] rounded-lg px-3 py-2.5 border border-slate-700">
                <Clock className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-gray-300">15 min to pay</span>
              </div>
            </div>
          </div>

          {/* RIGHT: Form */}
          <div className="lg:col-span-3">
            {enquirySubmitted ? (
              <Card className="bg-[#1e293b] border-slate-700 p-8" data-testid="enquiry-confirmed">
                <div className="text-center py-8">
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white heading-font mb-3">
                    Thanks, Your Booking Request Is In
                  </h2>
                  <p className="text-gray-400 text-sm mb-2 max-w-md mx-auto">
                    We will be in touch shortly to confirm your date and take payment.
                  </p>
                  <p className="text-gray-500 text-xs mb-8">
                    A confirmation email has been sent to <span className="text-gray-300">{form.email}</span>
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xs mx-auto">
                    <Link to="/" className="flex-1">
                      <Button type="button" className="w-full bg-[#e2e8f0] text-slate-900 hover:bg-[#cbd5e1] font-semibold h-11" data-testid="back-home-btn">
                        Back to Home
                      </Button>
                    </Link>
                    <Link to="/packages" className="flex-1">
                      <Button type="button" variant="outline" className="w-full border-slate-600 text-gray-300 hover:bg-slate-800 h-11" data-testid="view-packages-btn">
                        View Packages
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ) : (
            <Card className="bg-[#1e293b] border-slate-700 p-6" data-testid="booking-form-card">
              <h2 className="text-xl font-bold text-white heading-font mb-1">Book This Package</h2>
              <p className="text-sm text-gray-400 mb-6">Choose your dates, optional add-on, and confirm your booking.</p>

              {/* Calendar */}
              <div className="flex justify-center mb-5" data-testid="booking-calendar">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={disabledDays}
                  modifiers={{ range: rangeHighlight }}
                  modifiersClassNames={CALENDAR_MODIFIERS_CN}
                  className="rounded-lg border border-slate-600 bg-slate-800 text-white"
                  classNames={CALENDAR_CLASS_NAMES}
                />
              </div>

              {/* Date range + days selector */}
              <div className="mb-5 space-y-3">
                {selectedDate && (
                  <div className="bg-slate-800/60 rounded-lg px-4 py-3 flex items-center gap-3 border border-slate-600">
                    <CalendarDays className="w-5 h-5 text-[#e2e8f0] flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-white">{formatRange()}</p>
                      <p className="text-xs text-gray-400">{numDays} day{numDays > 1 ? 's' : ''}</p>
                    </div>
                    {rangeConflict && (
                      <Badge className="bg-red-500/20 text-red-400 border border-red-500/30 text-[10px]">Date conflict</Badge>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-sm text-gray-300 mb-2 block">Number of Days</Label>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={numDays <= 1}
                      onClick={() => setNumDays((n) => Math.max(1, n - 1))}
                      className="border-slate-600 text-gray-300 hover:bg-slate-700 h-10 w-10 p-0"
                      data-testid="days-minus"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-xl font-bold text-white w-10 text-center" data-testid="days-count">{numDays}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={numDays >= 14}
                      onClick={() => setNumDays((n) => Math.min(14, n + 1))}
                      className="border-slate-600 text-gray-300 hover:bg-slate-700 h-10 w-10 p-0"
                      data-testid="days-plus"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    {pricing.discountPct > 0 && (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs ml-2">
                        <Percent className="w-3 h-3 mr-1" />
                        {pricing.discountPct}% off
                      </Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1.5">10% off 3+ days &middot; 20% off 7+ days</p>
                </div>
              </div>

              {/* Add-on selector */}
              {addons.length > 0 && (
                <div className="mb-5" data-testid="addon-section">
                  <Label className="text-sm text-gray-300 mb-2 block">Optional Add-on</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {/* No addon option */}
                    <button
                      type="button"
                      onClick={() => setSelectedAddon(null)}
                      className={`text-left rounded-lg border px-3 py-2.5 transition-all text-sm ${
                        !selectedAddon
                          ? 'border-[#e2e8f0] bg-[#e2e8f0]/10 text-white'
                          : 'border-slate-600 text-gray-400 hover:border-slate-500'
                      }`}
                      data-testid="addon-none"
                    >
                      No add-on
                    </button>
                    {addons.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setSelectedAddon(a)}
                        className={`text-left rounded-lg border px-3 py-2.5 transition-all text-sm ${
                          selectedAddon?.id === a.id
                            ? 'border-[#e2e8f0] bg-[#e2e8f0]/10 text-white'
                            : 'border-slate-600 text-gray-400 hover:border-slate-500'
                        }`}
                        data-testid={`addon-${a.id}`}
                      >
                        <span className="font-medium text-gray-200">{a.name}</span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          +{pence(getCharge(a))}/day{a.quote_only ? ' deposit' : ''}
                        </span>
                        {a.quote_only && (
                          <span className="block text-[10px] text-gray-600 mt-0.5">Balance quoted after booking</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Customer form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <h3 className="text-lg font-bold text-white heading-font">Your Details</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-sm text-gray-300">Full Name <span className="text-red-400">*</span></Label>
                    <Input id="name" name="name" value={form.name} onChange={handleChange} required placeholder="John Smith"
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" data-testid="booking-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-sm text-gray-300">Phone <span className="text-red-400">*</span></Label>
                    <Input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} required placeholder="07xxx xxxxxx"
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" data-testid="booking-phone-input" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-sm text-gray-300">Email <span className="text-red-400">*</span></Label>
                  <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} required placeholder="john@example.com"
                    className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" data-testid="booking-email-input" />
                </div>

                {/* Pequod-specific fields */}
                {isPequod && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="loadInTime" className="text-sm text-gray-300">Load-in Time <span className="text-red-400">*</span></Label>
                        <Input id="loadInTime" name="loadInTime" value={form.loadInTime} onChange={handleChange}
                          placeholder="Earliest access time, e.g. 4pm"
                          className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                          data-testid="booking-load-in-time" />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="loadOutTime" className="text-sm text-gray-300">Load-out Time <span className="text-red-400">*</span></Label>
                        <Input id="loadOutTime" name="loadOutTime" value={form.loadOutTime} onChange={handleChange}
                          placeholder="Clear-out by, e.g. 2am"
                          className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                          data-testid="booking-load-out-time" />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="loadInAccess" className="text-sm text-gray-300">Load-in Access <span className="text-red-400">*</span></Label>
                      <select
                        id="loadInAccess"
                        name="loadInAccess"
                        value={form.loadInAccess}
                        onChange={handleChange}
                        className="flex h-10 w-full rounded-md border px-3 py-2 text-sm bg-slate-800 border-slate-600 text-white"
                        data-testid="booking-load-in-access"
                      >
                        <option value="" className="text-slate-500">Select access type</option>
                        <option value="Ground floor / step-free">Ground floor / step-free</option>
                        <option value="Lift available">Lift available</option>
                        <option value="Stairs, no lift">Stairs, no lift</option>
                        <option value="Other / not sure">Other / not sure</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="venueAccess" className="text-sm text-gray-300">Venue Access Notes <span className="text-red-400">*</span></Label>
                      <Textarea id="venueAccess" name="venueAccess" value={form.venueAccess} onChange={handleChange}
                        placeholder="Loading dock, parking, distance from vehicle to room, any restrictions"
                        rows={2}
                        className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 resize-none"
                        data-testid="booking-venue-access" />
                    </div>

                    <p className="text-xs text-gray-500 leading-relaxed bg-slate-800/40 rounded-lg px-3 py-2.5 border border-slate-700/50">
                      Difficult access (stairs without a lift, long carries or tight spaces) may require extra crew. If so, a surcharge of £100 to £150 per day applies. We will confirm this with you before your event. It is not charged now.
                    </p>
                  </>
                )}

                {/* Non-Pequod venue access (optional) */}
                {!isPequod && (
                  <div className="space-y-1.5">
                    <Label htmlFor="venueAccess" className="text-sm text-gray-300">Venue Access Notes <span className="text-gray-500">(optional)</span></Label>
                    <Textarea id="venueAccess" name="venueAccess" value={form.venueAccess} onChange={handleChange}
                      placeholder="Loading dock location, lift access, parking, setup times..."
                      rows={2}
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 resize-none"
                      data-testid="booking-venue-access" />
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="additionalInfo" className="text-sm text-gray-300">Additional Info <span className="text-gray-500">(optional)</span></Label>
                  <Textarea id="additionalInfo" name="additionalInfo" value={form.additionalInfo} onChange={handleChange}
                    placeholder="Special requirements, power needs, schedule notes..."
                    rows={2}
                    className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 resize-none"
                    data-testid="booking-additional-info" />
                </div>

                {/* Pricing breakdown */}
                <div className="border-t border-slate-600 pt-4 mt-6 space-y-2">
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>{pkg.name}{(pkg.quote_only || pkg.deposit_hold) ? ', deposit' : ''}/day</span>
                    <span className="text-gray-200">{pence(pricing.mainPerDay)}</span>
                  </div>
                  {selectedAddon && (
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>{selectedAddon.name}{selectedAddon.quote_only ? ', deposit' : ''}/day</span>
                      <span className="text-gray-200">+{pence(pricing.addonPerDay)}</span>
                    </div>
                  )}
                  {numDays > 1 && (
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>&times; {numDays} days</span>
                      <span className="text-gray-200">{pence(pricing.subtotal)}</span>
                    </div>
                  )}
                  {pricing.discountPct > 0 && (
                    <div className="flex justify-between text-sm text-emerald-400">
                      <span>{pricing.discountPct}% multi-day discount</span>
                      <span>-{pence(pricing.discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>Subtotal (ex VAT)</span>
                    <span className="text-gray-200">{pence(pricing.exVat)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>VAT (20%)</span>
                    <span className="text-gray-200">{pence(pricing.vat)}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                    <span className="text-sm font-medium text-gray-300">Pay now (inc VAT)</span>
                    <span className="text-2xl font-bold text-white" data-testid="total-deposit">{pence(pricing.total)}</span>
                  </div>

                  {/* Deposit / quote notes */}
                  {pricing.hasDepositHold && (
                    <p className="text-xs text-amber-400/80 leading-relaxed">
                      This is a deposit to hold your date. The remaining balance of {pence(pricing.balanceDuePence)} plus VAT will be invoiced before your event.
                    </p>
                  )}
                  {pricing.hasQuoteOnly && (
                    <p className="text-xs text-amber-400/80 leading-relaxed">
                      {selectedAddon?.quote_only
                        ? `Includes a deposit for the ${selectedAddon.name}. The band balance will be quoted and invoiced separately after booking.`
                        : `Includes a deposit for the ${pkg.name}. The balance will be quoted and invoiced separately after booking.`}
                    </p>
                  )}

                  {/* Footer line + action */}
                  {paymentsEnabled ? (
                    <>
                      <p className="text-xs text-gray-500">
                        {pricing.isDeposit
                          ? 'Deposit payment. You have 15 minutes to complete payment.'
                          : 'Full payment. You have 15 minutes to complete payment.'}
                      </p>

                      <Button
                        type="submit"
                        disabled={!canSubmit}
                        className="w-full bg-[#e2e8f0] text-slate-900 hover:bg-[#cbd5e1] font-semibold h-12 text-base disabled:opacity-40 mt-2"
                        data-testid="pay-deposit-btn"
                      >
                        {submitting ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <CreditCard className="w-5 h-5 mr-2" />
                        )}
                        {submitting ? 'Redirecting to payment...' : `Pay ${pence(pricing.total)}`}
                      </Button>

                      <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-gray-500">
                        <Lock className="w-3 h-3" /> Powered by Stripe — 256-bit SSL encrypted
                      </div>
                    </>
                  ) : (
                    <div className="mt-2 space-y-3" data-testid="booking-coming-soon">
                      <div className="bg-slate-800/60 border border-slate-600 rounded-lg px-4 py-3 text-center">
                        <p className="text-sm font-medium text-[#e2e8f0]">
                          Online booking opens soon. Submit your details and we will confirm your date.
                        </p>
                      </div>
                      <Button
                        type="submit"
                        disabled={!canSubmit}
                        className="w-full bg-[#e2e8f0] text-slate-900 hover:bg-[#cbd5e1] font-semibold h-12 text-base disabled:opacity-40"
                        data-testid="request-reserve-btn"
                      >
                        {submitting ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Send className="w-5 h-5 mr-2" />
                        )}
                        {submitting ? 'Submitting...' : 'Request to Reserve Your Date'}
                      </Button>
                    </div>
                  )}
                </div>
              </form>
            </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
